/**
 * @element qds-company-logo
 * 
 * A component that displays the company logo with optional linking.
 * Automatically switches between light and dark theme logos.
 * 
 * @attr {string} src - The URL of the logo image (default: QCG logo from CDN)
 * @attr {string} alt - Alternative text for the image (default: "QCG Logo")
 * @attr {number} height - Height of the logo in pixels (default: 32)
 * @attr {string} href - URL to link the logo to (default: "https://q360.ai")
 * @attr {string} target - Target for the link (default: "_blank")
 * @attr {boolean} q-only - Whether to use only the Q icon (default: true)
 * 
 * @csspart container - The container element
 * @csspart image - The logo image
 * @csspart link - The link wrapping the logo
 * 
 * @fires click - When the logo is clicked
 */

import { loadBaseCSS } from '../shared/button-utils.js';
import { ThemeChangeEventDetail } from '../../types/index';

// Load base CSS
loadBaseCSS();

interface LogoVariant {
  baseUrl: string;
  light: string;
  dark: string;
}

interface LogoUrls {
  qOnly: LogoVariant;
  full: LogoVariant;
}

const Q_ONLY_LOGO_PATH = 'M420 363.089C382.52 370.166 350 355.468 323.543 332.061C364.882 297.766 391.89 245.507 391.89 186.716C391.89 83.2873 310.315 0 209.449 0C174.173 0 141.654 10.3429 113.543 27.7625C57.3228 -5.98798 6.61417 2.17745 0 7.62106C37.4803 0.54436 70 15.2421 96.4567 38.6497C53.4646 72.9445 25.9055 126.292 25.9055 186.172C25.9055 289.6 107.48 372.888 208.346 372.888C244.724 372.888 278.346 362.001 307.008 343.492C362.677 376.154 413.386 368.533 420 363.089ZM208.346 363.634C128.976 363.634 64.4882 284.701 64.4882 187.26C64.4882 135.002 83.2283 87.6423 112.992 55.5249C163.15 109.961 190.157 191.071 190.157 191.071C190.157 191.071 224.331 278.169 291.024 332.061C267.874 351.658 239.213 363.634 208.346 363.634ZM223.228 170.93C197.323 104.517 163.701 63.6903 130.63 39.1941C153.228 21.2301 179.685 10.8872 208.898 10.8872C288.268 10.8872 352.756 89.8197 352.756 187.26C352.756 237.886 335.118 283.612 307.559 316.274C278.346 284.701 249.134 238.43 223.228 170.93Z';

export class QdsCompanyLogo extends HTMLElement {
  // Private properties
  private _qOnlyMode: boolean;
  private _logoUrls: LogoUrls;
  private _isDarkTheme: boolean;
  private _src: string;
  private _alt: string;
  private _height: number;
  private _href: string;
  private _target: string;
  private _customSrc: string | null;
  private _boundThemeChangeHandler: (event: CustomEvent<ThemeChangeEventDetail>) => void;

  static get observedAttributes(): string[] {
    return ['src', 'alt', 'height', 'href', 'target', 'q-only'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    
    // Initialize properties with defaults
    this._qOnlyMode = true; // Default to Q-only logo 
    
    // Logo URLs for Q-only and full logo variants
    this._logoUrls = {
      qOnly: {
        baseUrl: 'https://cdn.q360.ai/qed-design/QuantumLogos/Qicons/',
        light: 'QBlue-h420.svg',
        dark: 'QWhite-h420.svg'
      },
      full: {
        baseUrl: 'https://cdn.q360.ai/qed-design/QuantumLogos/QCG/',
        light: 'qcg-logo.svg',
        dark: 'qcg-logo-white.svg'
      }
    };
    
    this._isDarkTheme = this._readDocumentTheme();
    this._customSrc = null;
    
    // Get the appropriate logo URL based on theme
    this._src = this._getThemedLogoUrl();
    this._alt = 'QCG Logo';
    this._height = 32;
    this._href = '/';
    this._target = '';
    
    // Bind methods
    this._boundThemeChangeHandler = this._handleThemeChange.bind(this);
    
    this.render();
  }

  private _readDocumentTheme(): boolean {
    return document.documentElement.getAttribute('data-theme') === 'dark';
  }

  /**
   * Returns the appropriate logo URL based on the current theme and q-only setting
   */
  private _getThemedLogoUrl(): string {
    // If a custom src was provided, use that instead of the themed logos
    if (this._customSrc) {
      return this._customSrc;
    }
    
    // Get the appropriate logo configuration based on q-only mode
    const logoConfig = this._qOnlyMode ? this._logoUrls.qOnly : this._logoUrls.full;
    
    // Return the themed URL
    return logoConfig.baseUrl + (this._isDarkTheme ? logoConfig.dark : logoConfig.light);
  }

  connectedCallback(): void {
    if (!this.isConnected) return;
    
    // Listen for theme changes
    document.addEventListener('qds-theme-change', this._boundThemeChangeHandler as EventListener);
    
    const isDarkTheme = this._readDocumentTheme();
    if (this._isDarkTheme !== isDarkTheme) {
      this._isDarkTheme = isDarkTheme;
      this._src = this._getThemedLogoUrl();
      this.render();
    }
  }

  disconnectedCallback(): void {
    // Remove theme change listener
    document.removeEventListener('qds-theme-change', this._boundThemeChangeHandler as EventListener);
  }

  /**
   * Handle theme changes from the theme system
   */
  private _handleThemeChange(event: CustomEvent<ThemeChangeEventDetail>): void {
    const { dark } = event.detail;
    
    // Only update if theme has changed
    if (this._isDarkTheme !== dark) {
      this._isDarkTheme = dark;
      this._src = this._getThemedLogoUrl();
      this.render();
    }
  }

  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (oldValue === newValue) return;

    switch (name) {
      case 'src':
        // If newValue is null or empty, use the themed logo
        if (newValue === null || newValue === '') {
          this._customSrc = null;
          this._src = this._getThemedLogoUrl();
        } else {
          // Otherwise store the custom src
          this._customSrc = newValue;
          this._src = newValue;
        }
        break;
      case 'alt':
        this._alt = (newValue === null || newValue === '') ? this._alt : newValue;
        break;
      case 'height': {
        const height = parseInt(newValue, 10);
        this._height = isNaN(height) ? 32 : height;
        break;
      }
      case 'href':
        this._href = (newValue === null || newValue === '') ? this._href : newValue;
        break;
      case 'target':
        this._target = (newValue === null || newValue === '') ? this._target : newValue;
        break;
      case 'q-only':
        // Boolean attributes are present or not; when present, value is empty string
        this._qOnlyMode = newValue !== null && newValue !== 'false';
        
        // Update the src if we're not using a custom src
        if (!this._customSrc) {
          this._src = this._getThemedLogoUrl();
        }
        break;
    }

    this.render();
  }

  get src(): string {
    return this._src;
  }

  set src(value: string) {
    this.setAttribute('src', value);
  }

  get alt(): string {
    return this._alt;
  }

  set alt(value: string) {
    this.setAttribute('alt', value);
  }

  get height(): number {
    return this._height;
  }

  set height(value: number) {
    this.setAttribute('height', value.toString());
  }

  get href(): string {
    return this._href;
  }

  set href(value: string) {
    this.setAttribute('href', value);
  }

  get target(): string {
    return this._target;
  }

  set target(value: string) {
    this.setAttribute('target', value);
  }
  
  get qOnly(): boolean {
    return this._qOnlyMode;
  }
  
  set qOnly(value: boolean) {
    if (value) {
      this.setAttribute('q-only', '');
    } else {
      this.removeAttribute('q-only');
    }
  }

  private _handleClick(event: MouseEvent): void {
    // Dispatch a custom event when clicked
    this.dispatchEvent(new CustomEvent('click', {
      bubbles: true,
      composed: true
    }));
  }

  private _shouldRenderInlineQOnlyLogo(): boolean {
    return this._qOnlyMode && !this._customSrc;
  }

  private render(): void {
    if (!this.shadowRoot) return;
    
    const hasLink = Boolean(this._href);
    const renderInlineQOnlyLogo = this._shouldRenderInlineQOnlyLogo();
    const logoMarkup = renderInlineQOnlyLogo
      ? `
        <svg
          class="logo-svg"
          viewBox="0 0 420 373"
          role="img"
          aria-label="${this._alt}"
          part="image">
          <path d="${Q_ONLY_LOGO_PATH}" />
        </svg>
      `
      : `<img class="logo-image" src="${this._src}" alt="${this._alt}" part="image">`;
    
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: inline-block;
        }
        
        .container {
          display: flex;
          align-items: center;
          height: 100%;
        }
        
        .logo-image,
        .logo-svg {
          height: ${this._height}px;
          vertical-align: middle;
          margin: 0 var(--logo-margin, 12px);
          flex-shrink: 0;
        }

        .logo-image {
          object-fit: contain;
          transition: filter 0.3s ease;
        }

        .logo-svg {
          width: auto;
          overflow: visible;
          color: var(--company-logo-color, var(--theme-text-primary));
        }

        .logo-svg path {
          fill: currentColor;
        }
        
        a {
          display: flex;
          align-items: center;
          text-decoration: none;
        }
      </style>
      
      <div class="container" part="container">
        ${hasLink
          ? `<a href="${this._href}" target="${this._target}" part="link">
               ${logoMarkup}
             </a>`
          : `${logoMarkup}`
        }
      </div>
    `;
    
    // Add click handler
    const clickTarget = hasLink 
      ? this.shadowRoot.querySelector('a')
      : this.shadowRoot.querySelector(renderInlineQOnlyLogo ? 'svg' : 'img');
      
    if (clickTarget) {
      clickTarget.addEventListener('click', (e: Event) => this._handleClick(e as MouseEvent));
    }
  }
}

// Only define the custom element if it's not already defined
if (!customElements.get('qds-company-logo')) {
  customElements.define('qds-company-logo', QdsCompanyLogo);
}
